import React, { useState, useEffect, useCallback } from "react";
import {
  FaLink, FaTrash, FaCheck, FaExternalLinkAlt, FaBook,
  FaPlus, FaTimes, FaFilter, FaWhatsapp, FaBell, FaVial,
  FaChevronDown, FaChevronUp, FaPen,
} from "react-icons/fa";
import api from "../utils/axiosInstance";
import { useModal } from "../context/ModalContext";
import {
  PageHeader, Card, CardHeader, CardBody, Spinner, Button,
  Input, Select, InputGroup, EmptyState,
} from "../components/ui";

const EVENT_OPTIONS = [
  { key: "message", label: "Message Received", description: "When a new message arrives" },
  { key: "delivery", label: "Delivery Status", description: "When message delivery status updates" },
  { key: "status", label: "Connection Status", description: "When session connection changes" },
  { key: "messageSent", label: "Message Sent", description: "When an outgoing message is sent" },
  { key: "mediaMessageSent", label: "Media Sent", description: "When a media message is sent" },
  { key: "groupMessageSent", label: "Group Message Sent", description: "When a group message is sent" },
  { key: "bulkMessageSent", label: "Bulk Message Sent", description: "When a bulk campaign message is sent" },
];

const EMPTY_FORM = { webhookUrl: "", events: [], filterSenders: [], label: "" };

export default function WebhookIntegration() {
  const modal = useModal();

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionId, setSessionId] = useState("");

  const [webhooks, setWebhooks] = useState([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);

  // Form state for new/edit webhook
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [senderInput, setSenderInput] = useState("");
  const [saving, setSaving] = useState(false);

  // Expanded webhook detail
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    api.get("/api/sessions")
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        const list = Array.isArray(data) ? data : [];
        setSessions(list);
        const connected = list.filter((s) => s.status === "connected");
        if (connected.length > 0) setSessionId(connected[0].sessionId);
      })
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  }, []);

  const fetchWebhooks = useCallback(async () => {
    if (!sessionId) return;
    setWebhooksLoading(true);
    try {
      const res = await api.get(`/api/webhook/${sessionId}`);
      setWebhooks(Array.isArray(res.data.data) ? res.data.data : []);
    } catch {
      setWebhooks([]);
    } finally {
      setWebhooksLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) fetchWebhooks();
  }, [sessionId, fetchWebhooks]);

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setSenderInput("");
    setEditingId(null);
    setShowForm(false);
  };

  const openNewForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (wh) => {
    setForm({
      webhookUrl: wh.webhookUrl,
      events: [...wh.events],
      filterSenders: [...(wh.filterSenders || [])],
      label: wh.label || "",
    });
    setSenderInput("");
    setEditingId(wh._id);
    setShowForm(true);
    setExpandedId(null);
  };

  const toggleEvent = (key) => {
    setForm((prev) => ({
      ...prev,
      events: prev.events.includes(key)
        ? prev.events.filter((e) => e !== key)
        : [...prev.events, key],
    }));
  };

  const addSender = () => {
    const cleaned = senderInput.replace(/\D/g, "");
    if (cleaned.length < 7) {
      modal.alert("Enter a valid phone number (with country code)", "warning");
      return;
    }
    if (form.filterSenders.includes(cleaned)) {
      setSenderInput("");
      return;
    }
    setForm((prev) => ({
      ...prev,
      filterSenders: [...prev.filterSenders, cleaned],
    }));
    setSenderInput("");
  };

  const removeSender = (num) => {
    setForm((prev) => ({
      ...prev,
      filterSenders: prev.filterSenders.filter((s) => s !== num),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sessionId || !form.webhookUrl || form.events.length === 0) {
      modal.alert("Please fill in the webhook URL and select at least one event", "warning");
      return;
    }
    // Auto-add any typed but uncommitted sender filter
    let finalSenders = [...form.filterSenders];
    if (senderInput.trim()) {
      const cleaned = senderInput.replace(/\D/g, "");
      if (cleaned.length >= 7 && !finalSenders.includes(cleaned)) {
        finalSenders.push(cleaned);
      }
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/webhook/${editingId}`, {
          webhookUrl: form.webhookUrl,
          events: form.events,
          filterSenders: finalSenders,
          label: form.label,
        });
      } else {
        await api.post("/api/webhook/set", {
          sessionId,
          webhookUrl: form.webhookUrl,
          events: form.events,
          filterSenders: finalSenders,
          label: form.label,
        });
      }
      modal.alert(editingId ? "Webhook updated" : "Webhook created", "success");
      resetForm();
      fetchWebhooks();
    } catch {
      modal.alert("Failed to save webhook", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (wh) => {
    const ok = await modal.confirm(
      `Delete webhook "${wh.label || wh.webhookUrl}"?`,
      { danger: true, confirmText: "Delete" }
    );
    if (!ok) return;
    try {
      await api.delete(`/api/webhook/id/${wh._id}`);
      modal.alert("Webhook deleted", "success");
      fetchWebhooks();
    } catch {
      modal.alert("Failed to delete webhook", "error");
    }
  };

  const handleTest = async (wh) => {
    try {
      const res = await api.post("/api/webhook/test", { sessionId, webhookId: wh._id });
      modal.alert(res.data.message || "Test sent", "success");
    } catch {
      modal.alert("Test failed — check your webhook URL", "error");
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Webhook Integration"
        subtitle="Configure webhooks to receive real-time events from your WhatsApp sessions"
      />

      {/* Session Selector */}
      <Card className="mb-5">
        <CardBody>
          <InputGroup label="WhatsApp Session">
            {sessionsLoading ? (
              <div className="flex items-center gap-2 text-ink-muted text-sm py-2">
                <Spinner size="sm" /> Loading sessions...
              </div>
            ) : sessions.length > 0 ? (
              <Select value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                <option value="">-- Select a session --</option>
                {sessions.map((s) => (
                  <option key={s.sessionId} value={s.sessionId}>
                    {s.phone || s.sessionId} — {s.name || "WhatsApp Session"} {s.status !== "connected" ? `(${s.status})` : ""}
                  </option>
                ))}
              </Select>
            ) : (
              <div className="flex items-center gap-2 text-sm text-ink-muted py-2">
                <FaWhatsapp className="text-ink-faint" />
                No sessions found. Connect a WhatsApp number first.
              </div>
            )}
          </InputGroup>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Webhooks List + Form */}
        <div className="lg:col-span-2 space-y-5">
          {/* Active Webhooks */}
          <Card>
            <CardHeader className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FaBell size={13} className="text-brand" />
                <span className="font-semibold text-ink-primary text-sm">
                  Webhooks {webhooks.length > 0 && `(${webhooks.length})`}
                </span>
              </div>
              <Button variant="primary" size="sm" onClick={openNewForm} disabled={!sessionId}>
                <FaPlus size={10} className="mr-1.5" /> Add Webhook
              </Button>
            </CardHeader>

            {webhooksLoading ? (
              <CardBody>
                <div className="flex items-center justify-center py-8">
                  <Spinner size="md" />
                </div>
              </CardBody>
            ) : webhooks.length > 0 ? (
              <div className="divide-y divide-surface-border">
                {webhooks.map((wh) => (
                  <div key={wh._id} className="px-5 py-4">
                    {/* Webhook row */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                          <span className="text-sm font-semibold text-ink-primary truncate">
                            {wh.label || "Webhook"}
                          </span>
                        </div>
                        <p className="text-xs text-ink-muted font-mono truncate mb-2">
                          {wh.webhookUrl}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {wh.events.map((ev) => (
                            <span key={ev} className="badge-brand text-2xs">{ev}</span>
                          ))}
                          {wh.filterSenders?.length > 0 && (
                            <span className="badge-info text-2xs flex items-center gap-1">
                              <FaFilter size={8} /> {wh.filterSenders.length} sender filter{wh.filterSenders.length > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => setExpandedId(expandedId === wh._id ? null : wh._id)}
                          className="btn-icon"
                          title="Details"
                        >
                          {expandedId === wh._id ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                        </button>
                        <button onClick={() => handleTest(wh)} className="btn-icon" title="Test webhook">
                          <FaVial size={10} />
                        </button>
                        <button onClick={() => openEditForm(wh)} className="btn-icon hover:!text-brand" title="Edit webhook">
                          <FaPen size={10} />
                        </button>
                        <button onClick={() => handleDelete(wh)} className="btn-icon hover:!text-red-500" title="Delete webhook">
                          <FaTrash size={10} />
                        </button>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {expandedId === wh._id && (
                      <div className="mt-3 p-3 rounded-lg bg-surface-bg border border-surface-border space-y-2">
                        <div>
                          <p className="text-xs text-ink-muted mb-1">Endpoint</p>
                          <p className="text-xs text-ink-primary font-mono break-all">{wh.webhookUrl}</p>
                        </div>
                        <div>
                          <p className="text-xs text-ink-muted mb-1">Subscribed Events</p>
                          <div className="flex flex-wrap gap-1.5">
                            {wh.events.map((ev) => (
                              <span key={ev} className="badge-brand text-2xs">{ev}</span>
                            ))}
                          </div>
                        </div>
                        {wh.filterSenders?.length > 0 && (
                          <div>
                            <p className="text-xs text-ink-muted mb-1">Sender Filter (fires only for these numbers)</p>
                            <div className="flex flex-wrap gap-1.5">
                              {wh.filterSenders.map((s) => (
                                <span key={s} className="badge-neutral text-2xs font-mono">{s}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4 text-xs text-ink-faint pt-1">
                          <span>Created: {new Date(wh.createdAt).toLocaleDateString()}</span>
                          <span>Updated: {new Date(wh.updatedAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <CardBody>
                <EmptyState
                  icon={FaLink}
                  title="No webhooks configured"
                  description="Add a webhook to start receiving real-time events from this session."
                />
              </CardBody>
            )}
          </Card>

          {/* Add/Edit Form */}
          {showForm && (
            <Card>
              <CardHeader className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaPlus size={12} className="text-brand" />
                  <span className="font-semibold text-ink-primary text-sm">
                    {editingId ? "Edit Webhook" : "New Webhook"}
                  </span>
                </div>
                <button onClick={resetForm} className="btn-icon"><FaTimes size={12} /></button>
              </CardHeader>
              <CardBody>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Label */}
                  <InputGroup label="Label (optional)">
                    <Input
                      placeholder="e.g. n8n Notifications, CRM Sync"
                      value={form.label}
                      onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                    />
                  </InputGroup>

                  {/* Webhook URL */}
                  <InputGroup label="Webhook URL">
                    <Input
                      type="url"
                      placeholder="https://your-server.com/webhook"
                      value={form.webhookUrl}
                      onChange={(e) => setForm((p) => ({ ...p, webhookUrl: e.target.value }))}
                      required
                    />
                  </InputGroup>

                  {/* Events */}
                  <InputGroup label="Events to Subscribe">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {EVENT_OPTIONS.map((ev) => (
                        <label
                          key={ev.key}
                          className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                            form.events.includes(ev.key)
                              ? "border-brand bg-brand/5"
                              : "border-surface-border hover:border-ink-faint"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={form.events.includes(ev.key)}
                            onChange={() => toggleEvent(ev.key)}
                            className="mt-0.5 accent-[var(--color-brand)]"
                          />
                          <div>
                            <span className="text-xs font-medium text-ink-primary">{ev.label}</span>
                            <p className="text-2xs text-ink-muted">{ev.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </InputGroup>

                  {/* Sender Filter */}
                  <InputGroup label={<span className="flex items-center gap-1.5"><FaFilter size={10} className="text-ink-faint" /> Sender Filter <span className="text-ink-faint font-normal">(optional)</span></span>}>
                    <p className="text-xs text-ink-muted mb-2">
                      Only fire this webhook when a message is from one of these numbers. Leave empty to receive all messages.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g. 919876543210"
                        value={senderInput}
                        onChange={(e) => setSenderInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); addSender(); }
                        }}
                      />
                      <Button type="button" variant="secondary" size="sm" onClick={addSender}>
                        <FaPlus size={10} />
                      </Button>
                    </div>
                    {form.filterSenders.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {form.filterSenders.map((num) => (
                          <span key={num} className="flex items-center gap-1.5 badge-neutral font-mono text-xs pl-2 pr-1 py-1">
                            {num}
                            <button
                              type="button"
                              onClick={() => removeSender(num)}
                              className="hover:text-red-500 transition-colors p-0.5"
                            >
                              <FaTimes size={8} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </InputGroup>

                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-2">
                    <Button type="submit" variant="primary" loading={saving} disabled={!sessionId}>
                      <FaCheck size={11} className="mr-1.5" />
                      {editingId ? "Update Webhook" : "Save Webhook"}
                    </Button>
                    <Button type="button" variant="secondary" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Right: Info panels */}
        <div className="space-y-5">
          {/* Quick Stats */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-semibold text-ink-primary mb-3">Quick Summary</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-ink-muted">Total Webhooks</span>
                  <span className="text-sm font-semibold text-ink-primary">{webhooks.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-ink-muted">With Sender Filters</span>
                  <span className="text-sm font-semibold text-ink-primary">
                    {webhooks.filter((w) => w.filterSenders?.length > 0).length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-ink-muted">Session</span>
                  <span className="text-xs font-mono text-ink-secondary">{sessionId || "—"}</span>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* API Documentation */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-semibold text-ink-primary mb-3">API Documentation</h3>
              <p className="text-xs text-ink-muted mb-4">
                Full API reference for messaging, webhooks, sessions, and more.
              </p>
              <a
                href="https://docs.walytic.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border border-surface-border hover:border-brand/50 hover:bg-brand/5 transition-all group"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(1,113,222,0.1)" }}>
                  <FaBook size={13} color="var(--color-brand)" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink-primary">API Reference</p>
                  <p className="text-xs text-ink-muted">docs.walytic.com</p>
                </div>
                <FaExternalLinkAlt size={10} className="text-ink-faint group-hover:text-brand" />
              </a>
            </CardBody>
          </Card>

          {/* Payload Example */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-semibold text-ink-primary mb-3">Webhook Payload Example</h3>
              <pre className="text-xs font-mono bg-surface-bg p-3 rounded-lg border border-surface-border overflow-x-auto text-ink-secondary leading-relaxed">
{`{
  "event": "message",
  "sessionId": "919876543210",
  "sender": "918123456789",
  "recipient": "919876543210",
  "content": "Hello!",
  "direction": "in",
  "timestamp": "2025-03-12T10:30:00Z"
}`}
              </pre>
            </CardBody>
          </Card>

          {/* Filter Tip */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-semibold text-ink-primary mb-2 flex items-center gap-2">
                <FaFilter size={11} className="text-brand" /> Sender Filters
              </h3>
              <p className="text-xs text-ink-muted leading-relaxed">
                Use sender filters to receive webhooks only when specific numbers message you.
                For example, add your CRM's number to get notified only for that contact.
                Leave the filter empty to receive all messages.
              </p>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
